import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import DebtorForm from '../Debtors/DebtorForm';
import { pushModalToStack, removeModalFromStack, isTopModal } from './modalStack';
import { formatLocalDate } from '../../utils/dateTime';
import UnsavedChangesPopover from './UnsavedChangesPopover';

const DebtorFormModal = ({ debtor, onSave, onCancel, showToast }) => {
  const normalizeDate = (value) => formatLocalDate(value || '') || (value || '');
  const initialData = {
    type: debtor?.type || 'legal',
    name: debtor?.name || '',
    legal_form: debtor?.legal_form || '',
    inn: debtor?.inn || '',
    kpp: debtor?.kpp || '',
    ogrn: debtor?.ogrn || '',
    snils: debtor?.snils || '',
    address: debtor?.address || '',
    email: debtor?.email || '',
    phone: debtor?.phone || '',
    case_number: debtor?.case_number || '',
    arbitration_court: debtor?.arbitration_court || '',
    case_act_court: debtor?.case_act_court || debtor?.arbitration_court || '',
    procedure: debtor?.procedure || '',
    case_act_date: normalizeDate(debtor?.case_act_date),
    case_act_type: debtor?.case_act_type || 'decision',
    manager_act_type: debtor?.manager_act_independent
      ? (debtor?.manager_act_type || debtor?.case_act_type || 'decision')
      : (debtor?.case_act_type || debtor?.manager_act_type || 'decision'),
    manager_act_date: debtor?.manager_act_independent
      ? normalizeDate(debtor?.manager_act_date)
      : (normalizeDate(debtor?.manager_act_date) || normalizeDate(debtor?.case_act_date)),
    manager_act_independent: !!debtor?.manager_act_independent,
    arbitration_manager: debtor?.arbitration_manager || '',
    manager_act_court: debtor?.manager_act_court || debtor?.arbitration_court || ''
  };

  const [formData, setFormData] = useState(initialData);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const modalIdRef = useRef(`debtor_modal_${Date.now()}_${Math.random().toString(16).slice(2)}`);
  const awaitSyncRef = useRef(true);
  const userTouchedRef = useRef(false);

  // Обновление формы при изменении debtor prop
  useEffect(() => {
    const newData = {
      type: debtor?.type || 'legal',
      name: debtor?.name || '',
      legal_form: debtor?.legal_form || '',
      inn: debtor?.inn || '',
      kpp: debtor?.kpp || '',
      ogrn: debtor?.ogrn || '',
      snils: debtor?.snils || '',
      address: debtor?.address || '',
      email: debtor?.email || '',
      phone: debtor?.phone || '',
      case_number: debtor?.case_number || '',
      arbitration_court: debtor?.arbitration_court || '',
      case_act_court: debtor?.case_act_court || debtor?.arbitration_court || '',
      procedure: debtor?.procedure || '',
      case_act_date: normalizeDate(debtor?.case_act_date),
      case_act_type: debtor?.case_act_type || 'decision',
      manager_act_type: debtor?.manager_act_independent
        ? (debtor?.manager_act_type || debtor?.case_act_type || 'decision')
        : (debtor?.case_act_type || debtor?.manager_act_type || 'decision'),
      manager_act_date: debtor?.manager_act_independent
        ? normalizeDate(debtor?.manager_act_date)
        : (normalizeDate(debtor?.manager_act_date) || normalizeDate(debtor?.case_act_date)),
      manager_act_independent: !!debtor?.manager_act_independent,
      arbitration_manager: debtor?.arbitration_manager || '',
      manager_act_court: debtor?.manager_act_court || debtor?.arbitration_court || ''
    };
    setFormData(newData);
    setHasChanges(false);
    awaitSyncRef.current = true;
    userTouchedRef.current = false;
  }, [debtor]);

  useEffect(() => {
    if (!awaitSyncRef.current) return;
    setHasChanges(false);
    awaitSyncRef.current = false;
  }, [formData]);

  useEffect(() => {
    const id = modalIdRef.current;
    pushModalToStack(id);
    return () => removeModalFromStack(id);
  }, []);

  // Обработка Escape с приоритетом
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (!isTopModal(modalIdRef.current)) return;
        // Если открыт диалог подтверждения, не обрабатываем здесь
        if (showConfirmDialog) {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleClose();
        return false;
      }
    };

    // Используем capture-фазу для перехвата события до других обработчиков
    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [hasChanges, showConfirmDialog]);

  // Обработка Escape для диалога подтверждения
  useEffect(() => {
    if (showConfirmDialog) {
      const handleConfirmEscape = (e) => {
        if (e.key === 'Escape') {
          if (!isTopModal(modalIdRef.current)) return;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setShowConfirmDialog(false);
          return false;
        }
      };

      document.addEventListener('keydown', handleConfirmEscape, { capture: true });
      return () => {
        document.removeEventListener('keydown', handleConfirmEscape, { capture: true });
      };
    }
  }, [showConfirmDialog]);

  const handleClose = () => {
    if (!userTouchedRef.current) {
      onCancel();
      return;
    }
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onCancel();
  };

  const handleSave = async (data, { exit } = {}) => {
    try {
      const result = await onSave(data, { exit });

      if (result === false) {
        return false;
      }
      const merged = typeof result === 'object' && result !== null ? { ...result, ...data } : data;
      if (data?.manager_act_independent) {
        merged.manager_act_independent = true;
        merged.manager_act_court = data?.manager_act_court || merged.manager_act_court || '';
      }
      if (data?.case_act_court) {
        merged.case_act_court = data.case_act_court;
      }
      const payload = merged;
      const snapshot = JSON.parse(JSON.stringify(payload));
      setFormData(snapshot);

      if (exit) {
        setShowConfirmDialog(false);
        onCancel();
      }

      return payload;
    } catch (error) {
      console.error('Error saving debtor:', error);
      return false;
    }
  };

  const modalContent = (
    <>
      <div className="modal-overlay entity-form-modal-overlay" style={{ zIndex: 3200 }}>
        <div className="modal modal-lg">
          <div className="modal-header">
            <h3>{debtor ? 'Редактирование должника' : 'Создание должника'}</h3>
            <button className="close-btn" onClick={handleClose}>&times;</button>
          </div>
          <div
            className="modal-body"
            onInputCapture={() => {
              userTouchedRef.current = true;
            }}
            onChangeCapture={() => {
              userTouchedRef.current = true;
            }}
          >
            <DebtorForm
              debtor={debtor}
              formData={formData}
              setFormData={setFormData}
              onSave={handleSave}
              onCancel={handleClose}
              showToast={showToast}
              isViewMode={false}
              onFormChange={(changed) => {
                if (!userTouchedRef.current && changed) {
                  return;
                }
                setHasChanges(changed);
              }}
            />
          </div>
        </div>
      </div>

      <UnsavedChangesPopover
        isOpen={showConfirmDialog}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmClose}
        zIndex={13300}
      />
    </>
  );

  return createPortal(modalContent, document.body);
};

export default DebtorFormModal;
